/**
 * FOOTER COMPONENT - LANDING PAGE FOOTER
 * ======================================
 * 
 * The footer appears at the bottom of the landing page and provides
 * navigation to various informational pages, resources, and company links.
 * Now features the custom brand logo for consistent branding.
 * 
 * FEATURES:
 * - Company branding with custom logo
 * - Product navigation links
 * - Resources section (Blog, Help Center, Best Practices)
 * - Company information links (About, Privacy, Terms, Contact)
 * - Responsive grid layout
 * - Consistent styling with the rest of the application
 */

import { Logo } from "./Logo";

interface FooterProps {
  onNavigateToBlog: () => void;
  onNavigateToHelpCenter: () => void;
  onNavigateToBestPractices: () => void;
  onNavigateToAbout: () => void;
  onNavigateToPrivacy: () => void;
  onNavigateToTerms: () => void;
  onNavigateToContact: () => void;
  onScrollToSection: (sectionId: string) => void;
  onOpenDemo: () => void;
}

export function Footer({
  onNavigateToBlog,
  onNavigateToHelpCenter,
  onNavigateToBestPractices,
  onNavigateToAbout,
  onNavigateToPrivacy,
  onNavigateToTerms,
  onNavigateToContact,
  onScrollToSection,
  onOpenDemo
}: FooterProps) {
  
  // Handle logo click - scroll to top of page
  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="border-t bg-muted/30">
      <div className="container py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          
          {/* COMPANY BRANDING SECTION */}
          <div>
            <div className="mb-4">
              <Logo 
                size="sm" 
                onClick={handleLogoClick}
                showText={true}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Make better decisions through thoughtful reflection and systematic analysis.
            </p>
          </div>
          
          {/* PRODUCT SECTION */}
          <div>
            <h4 className="font-medium mb-4">Product</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <button 
                  onClick={() => onScrollToSection('features')}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Features
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onScrollToSection('pricing')}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Pricing
                </button>
              </li>
              <li>
                <button 
                  onClick={onOpenDemo}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Demo
                </button>
              </li>
            </ul>
          </div>
          
          {/* RESOURCES SECTION */}
          <div>
            <h4 className="font-medium mb-4">Resources</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <button 
                  onClick={onNavigateToBlog}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Blog
                </button>
              </li>
              <li>
                <button 
                  onClick={onNavigateToHelpCenter}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Help Center
                </button>
              </li>
              <li>
                <button 
                  onClick={onNavigateToBestPractices}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Best Practices
                </button>
              </li>
            </ul>
          </div>
          
          {/* COMPANY SECTION */}
          <div>
            <h4 className="font-medium mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <button 
                  onClick={onNavigateToAbout}
                  className="hover:text-foreground transition-colors text-left"
                >
                  About
                </button>
              </li>
              <li>
                <button 
                  onClick={onNavigateToPrivacy}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Privacy
                </button>
              </li>
              <li>
                <button 
                  onClick={onNavigateToTerms}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Terms
                </button>
              </li>
              <li>
                <button 
                  onClick={onNavigateToContact}
                  className="hover:text-foreground transition-colors text-left"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>
        </div>
        
        {/* COPYRIGHT SECTION */}
        <div className="border-t mt-12 pt-6 text-center text-sm text-muted-foreground">
          © 2025 Decisionr.xyz. All rights reserved.
        </div>
      </div>
    </footer>
  );
}